import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Heart, HeartOff, Users } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface VenueFollowButtonProps {
  venueId: string;
  userId: string;
  userType: 'supabase' | 'internal';
  size?: 'sm' | 'default' | 'lg';
  variant?: 'default' | 'outline' | 'ghost';
}

export const VenueFollowButton: React.FC<VenueFollowButtonProps> = ({
  venueId,
  userId,
  userType,
  size = 'default',
  variant = 'outline'
}) => {
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);

  useEffect(() => {
    checkFollowStatus();
    loadFollowerCount();
  }, [venueId, userId]);

  const checkFollowStatus = async () => {
    try {
      const { data } = await supabase
        .from('venue_followers')
        .select('id')
        .eq('venue_id', venueId)
        .eq('follower_id', userId)
        .eq('follower_type', userType)
        .eq('is_active', true)
        .maybeSingle();

      setIsFollowing(!!data);
    } catch (error) {
      console.error('Error checking follow status:', error);
    }
  };

  const loadFollowerCount = async () => {
    try {
      const { count } = await supabase
        .from('venue_followers')
        .select('*', { count: 'exact', head: true })
        .eq('venue_id', venueId)
        .eq('is_active', true);

      setFollowerCount(count || 0);
    } catch (error) {
      console.error('Error loading follower count:', error);
    }
  };

  const handleToggleFollow = async () => {
    setLoading(true);
    try {
      if (isFollowing) {
        // Unfollow
        const { error } = await supabase
          .from('venue_followers')
          .update({ is_active: false })
          .eq('venue_id', venueId)
          .eq('follower_id', userId)
          .eq('follower_type', userType);

        if (error) throw error;
        
        setIsFollowing(false);
        setFollowerCount(prev => Math.max(0, prev - 1));
        toast.success('Unfollowed venue');
      } else {
        // Follow
        const { error } = await supabase
          .from('venue_followers')
          .upsert({
            venue_id: venueId,
            follower_id: userId,
            follower_type: userType,
            is_active: true
          }, {
            onConflict: 'venue_id,follower_id,follower_type'
          });

        if (error) throw error;
        
        setIsFollowing(true);
        setFollowerCount(prev => prev + 1);
        toast.success('Following venue');
      }
    } catch (error) {
      console.error('Error toggling follow:', error);
      toast.error('Failed to update follow status');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant="ghost"
      size={size}
      onClick={handleToggleFollow}
      disabled={loading}
      className={`p-2 ${isFollowing 
        ? 'bg-red-500 hover:bg-red-600 text-white' 
        : 'text-red-500 hover:text-red-600 hover:bg-red-500/10'
      }`}
    >
      <Heart 
        className={`h-4 w-4 ${isFollowing ? 'fill-current' : ''}`}
      />
    </Button>
  );
};